
import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { sanitizeHtml } from "@/utils/htmlUtils";
import { transformBlogUrls } from "@/utils/urlTransformer";
import DOMPurify from "dompurify";

interface BlogPostContentProps {
  content: string;
}

export const BlogPostContent = ({ content }: BlogPostContentProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Enhanced processing for blog post content
  useEffect(() => {
    if (contentRef.current) {
      // Handle links - transform URLs and set appropriate attributes
      const links = contentRef.current.querySelectorAll('a[href]');
      links.forEach(link => {
        if (link instanceof HTMLAnchorElement) {
          // Transform URLs if needed
          if (link.href.includes('blog.gildy.it') || 
              (link.href.includes('gildy.it') && !link.href.includes('www.gildy.it'))) {
            // Import the transformSingleUrl function from urlTransformer
            import('@/utils/urlTransformer').then(({ transformSingleUrl }) => {
              const transformedUrl = transformSingleUrl(link.href);
              if (transformedUrl !== link.href) {
                link.href = transformedUrl;
              }
            });
          }
          
          // Check if it's an internal or external link
          const isGildyDomain = link.hostname.includes('gildy.it') || link.hostname.includes('www.gildy.it');
          
          if (isGildyDomain) {
            // Internal links - open in same tab
            link.removeAttribute('target');
            link.removeAttribute('rel');
          } else {
            // External links - open in new tab with security attributes
            link.setAttribute('rel', 'noopener nofollow');
            link.setAttribute('target', '_blank');
          }
        }
      });
      
      // Make tables responsive
      const tables = contentRef.current.querySelectorAll('table');
      tables.forEach(table => {
        const wrapper = document.createElement('div');
        wrapper.className = 'overflow-x-auto my-4 w-full';
        // Only wrap if not already wrapped
        if (table.parentNode?.nodeName !== 'DIV' || 
            !(table.parentNode as HTMLElement).classList.contains('overflow-x-auto')) {
          table.parentNode?.insertBefore(wrapper, table);
          wrapper.appendChild(table);
          table.style.width = '100%';
        }
      });
      
      // Enhance pre and code elements
      const codeBlocks = contentRef.current.querySelectorAll('pre');
      codeBlocks.forEach(block => {
        block.classList.add('rounded-md', 'my-4', 'w-full');
        block.style.maxWidth = '100%';
      });
      
      // Enhance images
      const images = contentRef.current.querySelectorAll('img');
      images.forEach(img => {
        img.classList.add('rounded-md', 'my-6', 'mx-auto');
        // Add loading lazy for performance
        img.setAttribute('loading', 'lazy');
        // Set max-width to ensure images don't overflow on mobile
        img.style.maxWidth = '100%';
      });
      
      // Make other potentially wide elements responsive
      const wideElements = contentRef.current.querySelectorAll('iframe, video, div');
      wideElements.forEach(el => {
        if (el.tagName.toLowerCase() === 'iframe' || el.tagName.toLowerCase() === 'video') {
          el.setAttribute('width', '100%');
          const wrapper = document.createElement('div');
          wrapper.className = 'w-full my-4';
          // Only wrap if not already wrapped
          if (el.parentNode?.nodeName !== 'DIV') {
            el.parentNode?.insertBefore(wrapper, el);
            wrapper.appendChild(el);
          }
        }
      });
    }
  }, [content]);
  
  // Apply sanitization and URL transformation to the HTML content
  const sanitizedContent = transformBlogUrls(
    DOMPurify.sanitize(content, {
      ADD_TAGS: ['iframe'],
      ADD_ATTR: ['frameborder', 'allowfullscreen', 'allow', 'width', 'height', 'style']
    })
  );
  
  return (
    <motion.div 
      ref={contentRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.4 }}
      className="blog-content-wrapper w-full"
    >
      <article 
        className="blog-content prose prose-lg dark:prose-invert mx-auto mb-8 w-full max-w-none"
        itemScope 
        itemType="https://schema.org/BlogPosting"
        data-ssr-rendered={false}
      >
        <div 
          className="blog-post-content"
          dangerouslySetInnerHTML={{ __html: sanitizedContent }} 
        />
      </article>
    </motion.div>
  );
};
